import {
  Box,
  Header,
  StatusIndicator,
  Table,
  TableProps,
} from "@amzn/awsui-components-console";
import React, { useMemo } from "react";

import { AnalyticsModelRow } from "@/hooks/admin/useAdminUserAnalytics";

import { formatShare, formatTokens, formatUsd } from "./format";

interface ModelBreakdownTableProps {
  rows: AnalyticsModelRow[];
  loading?: boolean;
}

/**
 * Token usage per model, with cost where a rate is known.
 *
 * A row without a rate shows that it is unpriced rather than a cost of zero;
 * summing the visible costs is therefore expected to fall short of the true
 * spend, and the unpriced rows are what accounts for the difference.
 */
const ModelBreakdownTable: React.FC<ModelBreakdownTableProps> = ({
  rows,
  loading = false,
}) => {
  // This table shows every model at once — no collection, no paging — so a
  // default order is a sorted copy rather than a sorting field. `[...rows]`
  // matters: `rows` is the analytics hook's state and `sort` is in-place.
  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => a.modelId.localeCompare(b.modelId)),
    [rows]
  );

  const columnDefinitions = useMemo<
    TableProps<AnalyticsModelRow>["columnDefinitions"]
  >(
    () => [
      {
        id: "modelId",
        header: "Model",
        cell: (row) => <Box variant="strong">{row.modelId}</Box>,
      },
      {
        id: "totalTokens",
        header: "Total tokens",
        cell: (row) => formatTokens(row.totalTokens),
      },
      {
        id: "tokenShare",
        header: "Share",
        cell: (row) => formatShare(row.tokenShare),
        width: 100,
      },
      {
        id: "inputTokens",
        header: "Input",
        cell: (row) => formatTokens(row.inputTokens),
      },
      {
        id: "outputTokens",
        header: "Output",
        cell: (row) => formatTokens(row.outputTokens),
      },
      {
        id: "estimatedCostUsd",
        header: "Estimated cost",
        cell: (row) =>
          row.isPriced ? (
            formatUsd(row.estimatedCostUsd)
          ) : (
            // Not a formatting nicety: a blank or $0.00 here would understate
            // spend and hide that a rate is missing for this model.
            <StatusIndicator type="warning" colorOverride="grey">
              No rate configured
            </StatusIndicator>
          ),
      },
    ],
    []
  );

  return (
    <Table
      data-testid="model-breakdown-table"
      variant="container"
      columnDefinitions={columnDefinitions}
      items={orderedRows}
      loading={loading}
      loadingText="Loading model usage"
      trackBy="modelId"
      empty={
        <Box textAlign="center" padding="l">
          <Box variant="h4">No model usage</Box>
          <Box variant="p" color="text-body-secondary">
            No questions were answered in this period.
          </Box>
        </Box>
      }
      header={
        <Header variant="h2" counter={`(${rows.length})`}>
          Usage by model
        </Header>
      }
    />
  );
};

export default ModelBreakdownTable;
